import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api, { fileUrl, BACKEND_URL } from '../lib/api';
import { Play, Pause, Upload as UploadIcon, Volume2, Sliders, Disc3, Music, Circle, Radio, Square, Video, Mic, X, Users, CheckCircle2, Settings, Download, Zap, Grid3x3, Rocket, Link2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DeckPanel } from '../components/decks/DeckPanel';
import { CenterConsole } from '../components/decks/CenterConsole';
import { fmtTs } from '../components/decks/fmt';

/**
 * Live DJ Decks — two-deck mixer with crossfader, per-deck play/pause, volume,
 * playback-rate (tempo), 3-band EQ (low/mid/high), and a filter FX per deck.
 * All in-browser via the Web Audio API. Matches djay's core principle in a lightweight way.
 */

const useAudioCtx = () => {
  const ctxRef = useRef(null);
  const destRef = useRef(null);   // MediaStreamDestination for recording
  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
      destRef.current = ctxRef.current.createMediaStreamDestination();
    }
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    return ctxRef.current;
  }, []);
  const getRecordStream = useCallback(() => {
    getCtx();
    return destRef.current?.stream;
  }, [getCtx]);
  useEffect(() => () => { try { ctxRef.current?.close(); } catch {} }, []);
  return { getCtx, getRecordStream, destRef };
};

/** Build the deck audio graph. Returns handles to control gain/EQ/filter and play state. */
function useDeck({ getCtx, destRef }) {
  const audioElRef = useRef(null);
  const sourceRef = useRef(null);
  const gainRef = useRef(null);
  const lowRef = useRef(null);
  const midRef = useRef(null);
  const highRef = useRef(null);
  const filterRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [trackTitle, setTrackTitle] = useState(null);
  const [trackBpm, setTrackBpm] = useState(null);
  const [trackKey, setTrackKey] = useState(null);

  const ensureGraph = useCallback(() => {
    if (sourceRef.current) return;
    const ctx = getCtx();
    audioElRef.current.crossOrigin = 'anonymous';
    sourceRef.current = ctx.createMediaElementSource(audioElRef.current);
    lowRef.current = ctx.createBiquadFilter();
    lowRef.current.type = 'lowshelf'; lowRef.current.frequency.value = 200;
    midRef.current = ctx.createBiquadFilter();
    midRef.current.type = 'peaking'; midRef.current.frequency.value = 1000; midRef.current.Q.value = 0.7;
    highRef.current = ctx.createBiquadFilter();
    highRef.current.type = 'highshelf'; highRef.current.frequency.value = 3200;
    filterRef.current = ctx.createBiquadFilter();
    filterRef.current.type = 'lowpass'; filterRef.current.frequency.value = 20000;
    gainRef.current = ctx.createGain();
    sourceRef.current
      .connect(lowRef.current)
      .connect(midRef.current)
      .connect(highRef.current)
      .connect(filterRef.current)
      .connect(gainRef.current);
    // Split: gain -> speakers AND -> MediaStreamDestination (for recording)
    gainRef.current.connect(ctx.destination);
    if (destRef.current) gainRef.current.connect(destRef.current);
    if (pendingApplyRef.current) pendingApplyRef.current();
  }, [getCtx, destRef]);

  const pendingApplyRef = useRef(null);

  const load = useCallback((url, title, bpm = null, musicalKey = null) => {
    if (!audioElRef.current) {
      audioElRef.current = new Audio();
      audioElRef.current.crossOrigin = 'anonymous';
      audioElRef.current.preload = 'auto';
    }
    audioElRef.current.pause();
    audioElRef.current.src = url;
    audioElRef.current.load();
    setTrackTitle(title || 'Untitled');
    setTrackBpm(bpm);
    setTrackKey(musicalKey);
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(async () => {
    if (!audioElRef.current || !audioElRef.current.src) return;
    ensureGraph();
    if (audioElRef.current.paused) {
      // Guard the play() promise: AbortError fires when the user hits pause before
      // the media starts, NotAllowedError fires when autoplay is blocked. Both
      // should just reset isPlaying rather than escalate to the CRA error overlay.
      try {
        await audioElRef.current.play();
        setIsPlaying(true);
      } catch (err) {
        setIsPlaying(false);
        if (err?.name && err.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('deck play() rejected:', err.name, err.message);
        }
      }
    } else {
      audioElRef.current.pause();
      setIsPlaying(false);
    }
  }, [ensureGraph]);

  const setGain = (v) => { if (gainRef.current) gainRef.current.gain.value = v; };
  const setLow = (db) => { if (lowRef.current) lowRef.current.gain.value = db; };
  const setMid = (db) => { if (midRef.current) midRef.current.gain.value = db; };
  const setHigh = (db) => { if (highRef.current) highRef.current.gain.value = db; };
  const setFilterFreq = (hz) => { if (filterRef.current) filterRef.current.frequency.value = hz; };
  const setRate = (r) => { if (audioElRef.current) audioElRef.current.playbackRate = r; };

  return {
    audioEl: audioElRef,
    isPlaying,
    trackTitle,
    trackBpm,
    trackKey,
    load,
    toggle,
    setGain,
    setLow, setMid, setHigh,
    setFilterFreq,
    setRate,
  };
}


export const Decks = () => {
  const { getCtx, getRecordStream, destRef } = useAudioCtx();
  const deckA = useDeck({ getCtx, destRef });
  const deckB = useDeck({ getCtx, destRef });
  const navigate = useNavigate();

  const [crossfade, setCrossfade] = useState(0.5);   // 0 = A, 1 = B
  const [masterVol, setMasterVol] = useState(0.9);
  const [tempoA, setTempoA] = useState(1);
  const [tempoB, setTempoB] = useState(1);
  const [lowA, setLowA] = useState(0), [midA, setMidA] = useState(0), [highA, setHighA] = useState(0), [filterA, setFilterA] = useState(20000);
  const [lowB, setLowB] = useState(0), [midB, setMidB] = useState(0), [highB, setHighB] = useState(0), [filterB, setFilterB] = useState(20000);
  const [library, setLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [midiStatus, setMidiStatus] = useState('idle');  // 'idle' | 'scanning' | 'connected'
  const [midiDeviceName, setMidiDeviceName] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [savingMix, setSavingMix] = useState(false);
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordStartRef = useRef(0);
  const recordTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // ============ Record master output & post to feed ============
  const startRecording = () => {
    const stream = getRecordStream();
    if (!stream) { toast.error('Audio not ready yet — hit Play once first'); return; }
    recordChunksRef.current = [];
    const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
    mr.ondataavailable = (e) => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
    mr.start(500);
    recorderRef.current = mr;
    recordStartRef.current = Date.now();
    setRecording(true);
    setRecordSecs(0);
    // Reset snapshot state and snap one frame right away
    snapshotsRef.current = [];
    const first = takeSnapshot();
    if (first) snapshotsRef.current.push({ dataUrl: first, at: 0 });
    recordTimerRef.current = setInterval(() => {
      setRecordSecs(Math.floor((Date.now() - recordStartRef.current) / 1000));
    }, 500);
    // Auto-snap a frame every 4s, capped at 10 snapshots
    snapshotTimerRef.current = setInterval(() => {
      if (snapshotsRef.current.length >= 10) return;
      const snap = takeSnapshot();
      if (snap) snapshotsRef.current.push({ dataUrl: snap, at: Math.floor((Date.now() - recordStartRef.current) / 1000) });
    }, 4000);
    toast.success('Recording your live mix...');
  };

  const stopRecordingAndPost = async () => {
    const mr = recorderRef.current;
    if (!mr) return;
    setRecording(false);
    clearInterval(recordTimerRef.current);
    clearInterval(snapshotTimerRef.current);
    // Grab one final snapshot for the end-of-mix frame
    const last = takeSnapshot();
    if (last && snapshotsRef.current.length < 10) {
      snapshotsRef.current.push({ dataUrl: last, at: Math.floor((Date.now() - recordStartRef.current) / 1000) });
    }
    const blob = await new Promise((resolve) => {
      mr.onstop = () => resolve(new Blob(recordChunksRef.current, { type: mr.mimeType || 'audio/webm' }));
      mr.stop();
    });
    setSavingMix(true);
    try {
      // Server audio ext whitelist doesn't include webm — pipe through ffmpeg extract-audio (mp4 shim).
      const fileMp4 = new File([blob], `livemix-${Date.now()}.mp4`, { type: 'video/mp4' });
      const form = new FormData();
      form.append('file', fileMp4);
      const { data } = await api.post('/upload/extract-audio', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
      setPendingMix({ audio_path: data.path, duration: recordSecs });
      setSelectedCoverIdx(0);
      setMixTitle(`Live Mix · ${new Date().toLocaleString()}`);
      // Show cover picker if we captured at least one frame, otherwise post straight away
      if (snapshotsRef.current.length > 0) {
        setShowCoverPicker(true);
      } else {
        await finalisePost({ audio_path: data.path, duration: recordSecs }, null, `Live Mix · ${new Date().toLocaleString()}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the mix');
    } finally {
      setSavingMix(false);
    }
  };

  const finalisePost = async (mix, coverDataUrl, titleOverride = null) => {
    setSavingCover(true);
    try {
      let coverPath = null;
      if (coverDataUrl) {
        // dataURL → Blob → upload
        const res = await fetch(coverDataUrl);
        const blob = await res.blob();
        const file = new File([blob], `cover-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const form = new FormData();
        form.append('file', file);
        const up = await api.post('/upload/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        coverPath = up.data.path;
      }
      const title = (titleOverride ?? mixTitle).trim() || `Live Mix · ${new Date().toLocaleString()}`;
      await api.post('/posts', {
        title,
        description: `Recorded live on RAW MIXES decks · ${mix.duration}s`,
        genre: 'House',
        tags: ['live', 'raw-decks'],
        audio_path: mix.audio_path,
        cover_path: coverPath,
        duration: mix.duration,
        media_type: 'audio',
      });
      toast.success('Live mix posted to your feed');
      setShowCoverPicker(false);
      setPendingMix(null);
      setMixTitle('');
      snapshotsRef.current = [];
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not post the mix');
    } finally {
      setSavingCover(false);
    }
  };
  // =============================================================

  // Derived per-deck gains (equal-power crossfade)
  const gainA = Math.cos(crossfade * Math.PI / 2) * masterVol;
  const gainB = Math.sin(crossfade * Math.PI / 2) * masterVol;

  useEffect(() => { deckA.setGain(gainA); }, [gainA, deckA]);
  useEffect(() => { deckB.setGain(gainB); }, [gainB, deckB]);

  // ============ Web MIDI auto-connect + SysEx firmware handshake ============
  const [midiOutput, setMidiOutput] = useState(null);
  const [hardwareDevice, setHardwareDevice] = useState(null);

  // ============ MIDI mapping (per-user, persistent) ============
  // Function catalog surfaced in the mapping modal. Keys must match the strings
  // that handleMidiMessage dispatches on so bindings resolve at MIDI-message time.
  const FUNCTION_CATALOG = useMemo(() => [
    { key: 'deckA.play',       label: 'Deck A · Play/Pause', kind: 'toggle' },
    { key: 'deckB.play',       label: 'Deck B · Play/Pause', kind: 'toggle' },
    { key: 'deckA.low',        label: 'Deck A · Low EQ',     kind: 'knob' },
    { key: 'deckA.mid',        label: 'Deck A · Mid EQ',     kind: 'knob' },
    { key: 'deckA.high',       label: 'Deck A · High EQ',    kind: 'knob' },
    { key: 'deckA.filter',     label: 'Deck A · Filter',     kind: 'knob' },
    { key: 'deckA.tempo',      label: 'Deck A · Tempo',      kind: 'knob' },
    { key: 'deckB.low',        label: 'Deck B · Low EQ',     kind: 'knob' },
    { key: 'deckB.mid',        label: 'Deck B · Mid EQ',     kind: 'knob' },
    { key: 'deckB.high',       label: 'Deck B · High EQ',    kind: 'knob' },
    { key: 'deckB.filter',     label: 'Deck B · Filter',     kind: 'knob' },
    { key: 'deckB.tempo',      label: 'Deck B · Tempo',      kind: 'knob' },
    { key: 'master.crossfader',label: 'Master · Crossfader', kind: 'knob' },
    { key: 'master.volume',    label: 'Master · Volume',     kind: 'knob' },
    ...[0,1,2,3,4,5,6,7].map(i => ({ key: `deckA.cue${i}`, label: `Deck A · Cue ${i+1}`, kind: 'toggle' })),
    ...[0,1,2,3,4,5,6,7].map(i => ({ key: `deckB.cue${i}`, label: `Deck B · Cue ${i+1}`, kind: 'toggle' })),
    ...[0,1,2,3].map(i => ({ key: `deckA.loop${i}`, label: `Deck A · Loop ${i+1}`, kind: 'toggle' })),
    ...[0,1,2,3].map(i => ({ key: `deckB.loop${i}`, label: `Deck B · Loop ${i+1}`, kind: 'toggle' })),
  ], []);
  const [midiMap, setMidiMap] = useState({});         // { "<status>:<data1>": {function, kind} }
  const midiMapRef = useRef({});
  useEffect(() => { midiMapRef.current = midiMap; }, [midiMap]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [learnFn, setLearnFn] = useState(null);       // {key,label,kind} while learning

  // Load user's saved mapping on mount
  useEffect(() => {
    api.get('/midi/mapping').then(({ data }) => {
      const m = {};
      (data.bindings || []).forEach(b => { m[b.key] = { function: b.function, kind: b.kind }; });
      setMidiMap(m);
    }).catch(() => {});
  }, []);

  const persistMapping = useCallback((next) => {
    const bindings = Object.entries(next).map(([key, v]) => ({ key, function: v.function, kind: v.kind }));
    api.put('/midi/mapping', { bindings }).catch(() => {});
  }, []);

  const bindLearned = useCallback((status, data1) => {
    if (!learnFn) return false;
    const key = `${status.toString(16).padStart(2,'0').toUpperCase()}:${data1.toString(16).padStart(2,'0').toUpperCase()}`;
    setMidiMap(prev => {
      // Remove any other binding with the same function so a control only appears once
      const cleaned = Object.fromEntries(Object.entries(prev).filter(([, v]) => v.function !== learnFn.key));
      cleaned[key] = { function: learnFn.key, kind: learnFn.kind };
      persistMapping(cleaned);
      return cleaned;
    });
    toast.success(`${learnFn.label} → ${key}`);
    setLearnFn(null);
    return true;
  }, [learnFn, persistMapping]);

  const clearBinding = useCallback((functionKey) => {
    setMidiMap(prev => {
      const next = Object.fromEntries(Object.entries(prev).filter(([, v]) => v.function !== functionKey));
      persistMapping(next);
      return next;
    });
  }, [persistMapping]);

  const resetMapping = useCallback(() => {
    setMidiMap({});
    persistMapping({});
    toast.success('Reverted to factory MIDI mapping');
  }, [persistMapping]);

  // ============ Firmware updater ============
  const [showFirmwareModal, setShowFirmwareModal] = useState(false);
  const [firmwareManifest, setFirmwareManifest] = useState(null);
  const [firmwareStatus, setFirmwareStatus] = useState('idle'); // idle | pushing | done | error

  // ============ Deck diagnostics ============
  const [showDiagnosticsModal, setShowDiagnosticsModal] = useState(false);
  const [diagHits, setDiagHits] = useState({});  // "status:data1" -> {status,data1,label,count,lastVal}

  const startDiagnostics = useCallback(() => {
    setDiagHits({});
    setShowDiagnosticsModal(true);
  }, []);

  const recordDiagnosticsHit = useCallback((status, data1, data2) => {
    const key = `${status.toString(16).padStart(2,'0').toUpperCase()}:${data1.toString(16).padStart(2,'0').toUpperCase()}`;
    setDiagHits(prev => ({
      ...prev,
      [key]: {
        status, data1,
        count: (prev[key]?.count || 0) + 1,
        lastVal: data2,
        kind: (status & 0xF0) === 0x90 ? 'note' : ((status & 0xF0) === 0xB0 ? 'cc' : 'other'),
      },
    }));
  }, []);

  // ============ Warranty registration ============
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [warranty, setWarranty] = useState({ owner_name: '', email: '', phone: '', country: 'GB', purchased_from: '' });
  const [warrantyExisting, setWarrantyExisting] = useState(null);
  const [warrantyBusy, setWarrantyBusy] = useState(false);

  const openWarranty = useCallback(async () => {
    if (!hardwareDevice) { toast.error('Connect your deck first so we can capture the serial'); return; }
    setShowWarrantyModal(true);
    try {
      const { data } = await api.get('/hardware/warranty');
      const existing = (data || []).find(w => w.device_id === hardwareDevice.device_id);
      if (existing) {
        setWarrantyExisting(existing);
        setWarranty({
          owner_name: existing.owner_name || '',
          email: existing.email || '',
          phone: existing.phone || '',
          country: existing.country || 'GB',
          purchased_from: existing.purchased_from || '',
        });
      } else {
        setWarrantyExisting(null);
      }
    } catch {}
  }, [hardwareDevice]);

  const submitWarranty = useCallback(async () => {
    if (!warranty.owner_name.trim() || !warranty.email.trim()) {
      toast.error('Name and email are required'); return;
    }
    setWarrantyBusy(true);
    try {
      const { data } = await api.post('/hardware/warranty', {
        device_id: hardwareDevice.device_id,
        ...warranty,
      });
      setWarrantyExisting(data);
      toast.success('Warranty registered');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not register warranty');
    } finally {
      setWarrantyBusy(false);
    }
  }, [warranty, hardwareDevice]);

  const openFirmwareModal = useCallback(async () => {
    setShowFirmwareModal(true);
    setFirmwareStatus('idle');
    try {
      const { data } = await api.get('/hardware/firmware/latest');
      setFirmwareManifest(data);
    } catch {
      toast.error('Could not reach the firmware server');
    }
  }, []);

  const pushFirmware = useCallback(() => {
    if (!firmwareManifest || !midiOutput) return;
    setFirmwareStatus('pushing');
    try {
      // Parse the hex string (space-separated) and send as a single SysEx block.
      // Real firmware payloads will be chunked; the deck's bootloader accepts blocks up to 512 bytes.
      const bytes = firmwareManifest.sysex_hex.trim().split(/\s+/).map(h => parseInt(h, 16));
      if (bytes[0] !== 0xF0 || bytes[bytes.length - 1] !== 0xF7) {
        throw new Error('Invalid firmware payload (must be a SysEx block)');
      }
      midiOutput.send(bytes);
      setFirmwareStatus('done');
      toast.success(`Firmware ${firmwareManifest.latest_version} pushed to deck`);
    } catch (err) {
      setFirmwareStatus('error');
      toast.error(err.message || 'Firmware push failed');
    }
  }, [firmwareManifest, midiOutput]);
  // ==========================================

  const dispatchFunction = useCallback((fnKey, data1, data2) => {
    // Knob-like values: normalise data2 (0..127) to the target range
    const norm = data2 / 127;
    switch (fnKey) {
      case 'deckA.play': if (data2 > 0) deckA.toggle(); return;
      case 'deckB.play': if (data2 > 0) deckB.toggle(); return;
      case 'deckA.low':    setLowA(-24 + norm * 36); return;
      case 'deckA.mid':    setMidA(-24 + norm * 36); return;
      case 'deckA.high':   setHighA(-24 + norm * 36); return;
      case 'deckA.filter': setFilterA(80 + norm * (20000 - 80)); return;
      case 'deckA.tempo':  setTempoA(0.5 + norm); return;
      case 'deckB.low':    setLowB(-24 + norm * 36); return;
      case 'deckB.mid':    setMidB(-24 + norm * 36); return;
      case 'deckB.high':   setHighB(-24 + norm * 36); return;
      case 'deckB.filter': setFilterB(80 + norm * (20000 - 80)); return;
      case 'deckB.tempo':  setTempoB(0.5 + norm); return;
      case 'master.crossfader': setCrossfade(norm); return;
      case 'master.volume':     setMasterVol(norm); return;
      default:
        // Cues + loops — read handlers from refs to avoid TDZ (they're declared further down)
        const m = fnKey.match(/^(deckA|deckB)\.(cue|loop)(\d)$/);
        if (m && data2 > 0) {
          const side = m[1] === 'deckA' ? 'A' : 'B';
          const idx = parseInt(m[3], 10);
          if (m[2] === 'cue' && cuePadRef.current) cuePadRef.current(side, idx, false);
          else if (loopPadRef.current) loopPadRef.current(side, idx, false);
        }
    }
  }, [deckA, deckB]);
  const cuePadRef = useRef(null);
  const loopPadRef = useRef(null);
  // ==============================================================

  const parseIdentityReply = useCallback((bytes) => {
    // Universal Non-Realtime Identity Reply:
    // F0 7E <device> 06 02 <mfr> <family lsb> <family msb> <member lsb> <member msb> <fw...> F7
    // Manufacturer ID is either 1 byte or 3 bytes (0x00 + 2). Handle both.
    if (bytes.length < 12 || bytes[0] !== 0xF0 || bytes[1] !== 0x7E || bytes[3] !== 0x06 || bytes[4] !== 0x02) {
      return null;
    }
    let idx = 5;
    let mfr;
    if (bytes[idx] === 0x00) { mfr = `00 ${bytes[idx+1].toString(16).padStart(2,'0')} ${bytes[idx+2].toString(16).padStart(2,'0')}`.toUpperCase(); idx += 3; }
    else { mfr = bytes[idx].toString(16).padStart(2,'0').toUpperCase(); idx += 1; }
    const familyLsb = bytes[idx++], familyMsb = bytes[idx++];
    const memberLsb = bytes[idx++], memberMsb = bytes[idx++];
    const fwBytes = [];
    while (idx < bytes.length - 1 && bytes[idx] !== 0xF7) fwBytes.push(bytes[idx++]);
    const family = `${familyMsb.toString(16).padStart(2,'0')}${familyLsb.toString(16).padStart(2,'0')}`.toUpperCase();
    const model = `${memberMsb.toString(16).padStart(2,'0')}${memberLsb.toString(16).padStart(2,'0')}`.toUpperCase();
    const firmware = fwBytes.map(b => b.toString(16).padStart(2,'0')).join('.').toUpperCase();
    // Some vendors encode a serial number in the firmware payload's trailing 4-8 bytes
    const serial = fwBytes.length >= 4 ? fwBytes.slice(-4).map(b => b.toString(16).padStart(2,'0')).join('').toUpperCase() : null;
    return {
      manufacturer_id: mfr,
      family_code: family,
      model_number: model,
      firmware_version: firmware,
      serial,
      raw_sysex: Array.from(bytes).map(b => b.toString(16).padStart(2,'0')).join(' ').toUpperCase(),
    };
  }, []);

  const registerHardware = useCallback(async (info, deviceName) => {
    try {
      const { data } = await api.post('/hardware/handshake', { ...info, device_name: deviceName });
      setHardwareDevice(data);
      toast.success(`Hardware linked: ${deviceName}${info.serial ? ` · #${info.serial}` : ''}`);
    } catch (err) {
      // Silent — handshake is analytics/warranty, not a blocker for using the deck
      console.warn('Hardware handshake failed', err);
    }
  }, []);

  useEffect(() => {
    if (!navigator.requestMIDIAccess) {
      setMidiStatus('unsupported');
      return;
    }
    let access = null;
    setMidiStatus('scanning');
    // Request SysEx so we can send an Identity Request and match the reply
    navigator.requestMIDIAccess({ sysex: true }).then((a) => {
      access = a;
      const attach = () => {
        let foundIn = null, foundOut = null;
        for (const input of access.inputs.values()) {
          if (/raw mixes|raw mini/i.test(input.name || '')) { foundIn = input; break; }
          if (!foundIn) foundIn = input;
        }
        for (const output of access.outputs.values()) {
          if (foundIn && output.name === foundIn.name) { foundOut = output; break; }
          if (!foundOut) foundOut = output;
        }
        if (foundIn) {
          // Bind through a ref-wrapper so every message dispatches through the
          // LATEST closure — otherwise Diagnostics / Learn / hardware handshake
          // capture stale state (showDiagnosticsModal=false, learnFn=null, midiDeviceName=null).
          foundIn.onmidimessage = (e) => midiHandlerRef.current && midiHandlerRef.current(e);
          setMidiStatus('connected');
          setMidiDeviceName(foundIn.name || 'MIDI device');
          if (foundOut) {
            setMidiOutput(foundOut);
            try { foundOut.send([0xF0, 0x7E, 0x7F, 0x06, 0x01, 0xF7]); }
            catch (e) { console.warn('SysEx identity request failed', e); }
          }
        } else {
          setMidiStatus('scanning');
          setMidiDeviceName(null);
          setMidiOutput(null);
        }
      };
      attach();
      access.onstatechange = attach;
    }).catch(() => setMidiStatus('denied'));
    return () => { if (access) access.onstatechange = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the ref pointed at the latest handler on every render/dep change.
  const midiHandlerRef = useRef(null);

  const handleMidiMessage = useCallback((e) => {
    const [status, data1, data2] = e.data;
    // SysEx (0xF0 ... 0xF7) — handle identity reply and register the device
    if (status === 0xF0) {
      const info = parseIdentityReply(e.data);
      if (info) {
        registerHardware(info, midiDeviceName || 'Unknown MIDI device');
      }
      return;
    }
    // Learn mode: intercept the very next control event and bind it
    const isNoteOrCC = ((status & 0xF0) === 0x90) || ((status & 0xF0) === 0xB0);
    // Feed the diagnostics tracker regardless of mapping
    if (isNoteOrCC && showDiagnosticsModal) {
      recordDiagnosticsHit(status, data1, data2 ?? 0);
    }
    if (isNoteOrCC && learnFn) {
      if (bindLearned(status, data1)) return;
    }
    // User-mapped bindings win over the factory mapping below
    const key = `${status.toString(16).padStart(2,'0').toUpperCase()}:${data1.toString(16).padStart(2,'0').toUpperCase()}`;
    const bound = midiMapRef.current[key];
    if (bound) {
      dispatchFunction(bound.function, data1, data2 ?? 0);
      return;
    }
    const type = status & 0xF0;
    const channel = status & 0x0F;   // 0 = Deck A, 1 = Deck B
    // Note On (0x90): 0x0B = PLAY toggle
    if (type === 0x90 && data2 > 0) {
      if (data1 === 0x0B) (channel === 0 ? deckA : deckB).toggle();
    }
    // Control Change (0xB0): faders/knobs. Value normalised 0..1
    if (type === 0xB0) {
      const v = data2 / 127;
      switch (data1) {
        case 0x07: // Volume — used as EQ Low
          if (channel === 0) setLowA((v - 0.5) * 24); else setLowB((v - 0.5) * 24);
          break;
        case 0x08: // Mid
          if (channel === 0) setMidA((v - 0.5) * 24); else setMidB((v - 0.5) * 24);
          break;
        case 0x09: // High
          if (channel === 0) setHighA((v - 0.5) * 24); else setHighB((v - 0.5) * 24);
          break;
        case 0x0A: // Filter (log-ish sweep from 80Hz to 20kHz)
          {
            const hz = 80 * Math.pow(20000 / 80, v);
            if (channel === 0) setFilterA(hz); else setFilterB(hz);
          }
          break;
        case 0x14: // Tempo fader
          if (channel === 0) setTempoA(0.5 + v); else setTempoB(0.5 + v);
          break;
        case 0x1F: // Crossfader
          setCrossfade(v);
          break;
        case 0x1E: // Master volume
          setMasterVol(v);
          break;
        default: break;
      }
    }
  }, [deckA, deckB, parseIdentityReply, registerHardware, midiDeviceName, learnFn, bindLearned, dispatchFunction, showDiagnosticsModal, recordDiagnosticsHit]);
  useEffect(() => { midiHandlerRef.current = handleMidiMessage; }, [handleMidiMessage]);

  // =========================================================================

  // Load user's own posts as track library
  useEffect(() => {
    api.get('/posts/feed').then(r => {
      setLibrary(r.data.filter(p => p.media_type !== 'video').slice(0, 30));
    }).catch(() => {});
  }, []);

  // Currently-loaded track paths per deck. Declared here (above the Track Ghost
  // memos) because anchorTrack reads them during render — declaring them further
  // down triggers a TDZ ReferenceError as soon as a track is loaded.
  const currentPathA = useRef(null);
  const currentPathB = useRef(null);

  // ============ Track Ghost — harmonic match ============
  // Compute compatibility of every library track against whichever deck was
  // loaded most recently, so DJs get a "next track" suggestion at a glance.
  const NOTE_ORDER = { C:0, 'C#':1, Db:1, D:2, 'D#':3, Eb:3, E:4, F:5, 'F#':6, Gb:6, G:7, 'G#':8, Ab:8, A:9, 'A#':10, Bb:10, B:11 };
  const parseKey = (k) => {
    if (!k) return null;
    const parts = k.trim().split(/\s+/);
    const tonic = parts[0];
    const mode = (parts[1] || 'major').toLowerCase();
    if (!(tonic in NOTE_ORDER)) return null;
    return { tonic: NOTE_ORDER[tonic], mode };
  };
  const harmonicScore = useCallback((a, b) => {
    if (!a || !b) return null;
    // Never suggest the anchor track itself
    if (a.audio_path && b.audio_path && a.audio_path === b.audio_path) return null;
    const bpmDiff = a.bpm && b.bpm ? Math.abs(a.bpm - b.bpm) : null;
    const ka = parseKey(a.musical_key), kb = parseKey(b.musical_key);
    if (!ka || !kb) {
      if (bpmDiff !== null && bpmDiff <= 3) return { label: 'BPM sync', badge: '±', tone: 'ok' };
      return null;
    }
    let semi = ((kb.tonic - ka.tonic) % 12 + 12) % 12;
    const sameMode = ka.mode === kb.mode;
    // Camelot-style compatibility:
    //  - Perfect: same key, same mode
    //  - +5th / +4th: 7 or 5 semitones apart, same mode (neighbours on the wheel)
    //  - Relative: 3 (major→minor of same key sig) or 9 (minor→major) semitones, opposite mode
    let harmony = null;
    if (semi === 0 && sameMode) harmony = 'Perfect';
    else if ((semi === 7 || semi === 5) && sameMode) harmony = semi === 7 ? '+5th' : '+4th';
    else if (!sameMode && ((ka.mode === 'major' && semi === 9) || (ka.mode === 'minor' && semi === 3))) harmony = 'Relative';
    if (!harmony) return null;
    if (bpmDiff !== null && bpmDiff > 8) return null;
    const tone = harmony === 'Perfect' ? 'perfect' : (harmony.startsWith('+') ? 'great' : 'ok');
    const badgeTxt = bpmDiff !== null ? `${harmony} · ${bpmDiff <= 3 ? 'BPM ✓' : `Δ${Math.round(bpmDiff)}`}` : harmony;
    return { label: harmony, badge: badgeTxt, tone };
  }, []);

  const anchorTrack = useMemo(() => {
    // Include audio_path so harmonicScore can exclude the anchor from its own suggestions
    const packA = (deckA.trackKey || deckA.trackBpm) ? { musical_key: deckA.trackKey, bpm: deckA.trackBpm, audio_path: currentPathA.current } : null;
    const packB = (deckB.trackKey || deckB.trackBpm) ? { musical_key: deckB.trackKey, bpm: deckB.trackBpm, audio_path: currentPathB.current } : null;
    if (deckA.isPlaying && packA) return packA;
    if (deckB.isPlaying && packB) return packB;
    return packA || packB || null;
  }, [deckA.trackKey, deckA.trackBpm, deckA.isPlaying, deckB.trackKey, deckB.trackBpm, deckB.isPlaying]);

  const scoredLibrary = useMemo(() => {
    if (!anchorTrack) return library.map(p => ({ ...p, __ghost: null }));
    const scored = library.map(p => ({ ...p, __ghost: harmonicScore(anchorTrack, p) }));
    scored.sort((a, b) => {
      const rank = g => g?.tone === 'perfect' ? 0 : g?.tone === 'great' ? 1 : g?.tone === 'ok' ? 2 : 3;
      return rank(a.__ghost) - rank(b.__ghost);
    });
    return scored;
  }, [library, anchorTrack, harmonicScore]);
  const ghostSuggestions = useMemo(() => scoredLibrary.filter(p => p.__ghost).slice(0, 3), [scoredLibrary]);
  // ==============================================================

  const loadIntoDeck = async (deck, post) => {
    deck.load(fileUrl(post.audio_path), post.title, post.bpm || null, post.musical_key || null);
    toast.success(`Loaded "${post.title}"${post.bpm ? ` · ${post.bpm} BPM` : ''}`);
    const target = deck === deckA ? 'A' : 'B';
    if (target === 'A') { currentPathA.current = post.audio_path; setActiveLoopA(-1); }
    else { currentPathB.current = post.audio_path; setActiveLoopB(-1); }
    // Broadcast key/BPM to other pages (Auto-Loop-Match reads this in Loop Station)
    try {
      const payload = {
        deck: target,
        key: post.musical_key || null,
        bpm: post.bpm || null,
        title: post.title,
        at: Date.now(),
      };
      localStorage.setItem('rawmixes.deck.current', JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent('rawmixes:deck-loaded', { detail: payload }));
    } catch {}
    const { cues, loops } = await fetchCues(post.audio_path);
    if (target === 'A') { setCuesA(cues); setLoopsA(loops); }
    else { setCuesB(cues); setLoopsB(loops); }
  };

  const handleFileUpload = async (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const targetDeck = side === 'A' ? deckA : deckB;
    setUploading(true);
    try {
      // Route through extract-audio if it's a video, otherwise /upload/audio
      const ext = file.name.split('.').pop().toLowerCase();
      const isVideo = ['mp4', 'mov', 'webm', 'm4v', 'mkv', 'avi'].includes(ext);
      const endpoint = isVideo ? '/upload/extract-audio' : '/upload/audio';
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(endpoint, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 900000,
      });
      targetDeck.load(fileUrl(data.path), file.name);
      // Persist the path + hydrate cues/loops so the pads and KILL VOX work
      // for uploaded files (not just tracks pulled from the library).
      if (side === 'A') { currentPathA.current = data.path; setActiveLoopA(-1); }
      else { currentPathB.current = data.path; setActiveLoopB(-1); }
      const { cues, loops } = await fetchCues(data.path);
      if (side === 'A') { setCuesA(cues); setLoopsA(loops); }
      else { setCuesB(cues); setLoopsB(loops); }
      toast.success(`Loaded "${file.name}"${data.extracted_from_video ? ' (audio extracted)' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const [uploadTarget, setUploadTarget] = useState(null);

  // ============ Cue points & loops (per user × per track) ============
  const [cuesA, setCuesA] = useState(() => Array(8).fill(null));
  const [cuesB, setCuesB] = useState(() => Array(8).fill(null));
  const [loopsA, setLoopsA] = useState(() => Array(4).fill(null));
  const [loopsB, setLoopsB] = useState(() => Array(4).fill(null));
  const [activeLoopA, setActiveLoopA] = useState(-1);
  const [activeLoopB, setActiveLoopB] = useState(-1);
  const saveTimerRef = useRef(null);

  const fetchCues = useCallback(async (path) => {
    try {
      const { data } = await api.get('/tracks/cues', { params: { path } });
      const cues = Array(8).fill(null);
      (data.cues || []).forEach(c => { if (c.slot >= 0 && c.slot < 8) cues[c.slot] = c; });
      const loops = Array(4).fill(null);
      (data.loops || []).forEach(l => { if (l.slot >= 0 && l.slot < 4) loops[l.slot] = l; });
      return { cues, loops };
    } catch {
      return { cues: Array(8).fill(null), loops: Array(4).fill(null) };
    }
  }, []);

  const persistCues = useCallback((path, cues, loops) => {
    if (!path) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const cuesList = cues.map((c, slot) => c ? { slot, position_sec: c.position_sec, label: c.label || null } : null).filter(Boolean);
      const loopsList = loops.map((l, slot) => (l && l.end_sec != null) ? { slot, start_sec: l.start_sec, end_sec: l.end_sec, label: l.label || null } : null).filter(Boolean);
      api.put('/tracks/cues', { audio_path: path, cues: cuesList, loops: loopsList }).catch(() => {});
    }, 400);
  }, []);

  const handleCuePad = useCallback((target, idx, shift = false) => {
    const deck = target === 'A' ? deckA : deckB;
    const audio = deck.audioEl.current;
    const path = target === 'A' ? currentPathA.current : currentPathB.current;
    if (!audio || !path) { toast.error('Load a track first'); return; }
    const prev = target === 'A' ? cuesA : cuesB;
    const setCues = target === 'A' ? setCuesA : setCuesB;
    const loops  = target === 'A' ? loopsA : loopsB;
    const next = prev.slice();
    if (shift) next[idx] = null;
    else if (next[idx]) { audio.currentTime = next[idx].position_sec; }
    else { next[idx] = { position_sec: Math.round(audio.currentTime * 100) / 100 }; }
    setCues(next);
    persistCues(path, next, loops);
  }, [deckA, deckB, cuesA, cuesB, loopsA, loopsB, persistCues]);

  const handleLoopPad = useCallback((target, idx, shift = false) => {
    const deck = target === 'A' ? deckA : deckB;
    const audio = deck.audioEl.current;
    const path = target === 'A' ? currentPathA.current : currentPathB.current;
    if (!audio || !path) { toast.error('Load a track first'); return; }
    const cues        = target === 'A' ? cuesA  : cuesB;
    const prev        = target === 'A' ? loopsA : loopsB;
    const setLoops    = target === 'A' ? setLoopsA : setLoopsB;
    const activeLoop  = target === 'A' ? activeLoopA : activeLoopB;
    const setActive   = target === 'A' ? setActiveLoopA : setActiveLoopB;
    const next = prev.slice();
    const cur = next[idx];
    if (shift) {
      next[idx] = null;
      if (activeLoop === idx) setActive(-1);
    } else if (!cur) {
      next[idx] = { start_sec: Math.round(audio.currentTime * 100) / 100, end_sec: null };
    } else if (cur.end_sec == null) {
      const end = Math.round(audio.currentTime * 100) / 100;
      if (end <= cur.start_sec) { next[idx] = null; toast.error('Loop end must come after start'); }
      else { next[idx] = { start_sec: cur.start_sec, end_sec: end }; setActive(idx); }
    } else if (activeLoop === idx) {
      setActive(-1);
    } else {
      audio.currentTime = cur.start_sec;
      setActive(idx);
    }
    setLoops(next);
    persistCues(path, cues, next);
  }, [deckA, deckB, cuesA, cuesB, loopsA, loopsB, activeLoopA, activeLoopB, persistCues]);
  // ============ Live Vocal Isolation (Demucs stems) ============
  const [demucsEnabled, setDemucsEnabled] = useState(false);
  const [vocalKilled, setVocalKilled] = useState({ A: false, B: false });
  const [vocalBusy, setVocalBusy] = useState({ A: false, B: false });
  const stemJobsRef = useRef({ A: null, B: null });

  useEffect(() => {
    api.get('/ai/status').then(({ data }) => setDemucsEnabled(!!data?.stem_split?.enabled)).catch(() => {});
  }, []);

  const toggleVocalKill = useCallback(async (target) => {
    const deck = target === 'A' ? deckA : deckB;
    const audioPath = target === 'A' ? currentPathA.current : currentPathB.current;
    if (!audioPath) { toast.error('Load a track into deck ' + target + ' first'); return; }
    if (!demucsEnabled) {
      toast.error('Add DEMUCS_WORKER_URL to backend/.env to unlock vocal isolation');
      return;
    }
    if (vocalKilled[target]) {
      deck.load(fileUrl(audioPath), deck.trackTitle || 'Track', deck.trackBpm, deck.trackKey);
      setVocalKilled(v => ({ ...v, [target]: false }));
      return;
    }
    setVocalBusy(b => ({ ...b, [target]: true }));
    try {
      let job = stemJobsRef.current[target];
      if (!job || job.audio_path !== audioPath) {
        const { data } = await api.post('/ai/stems', { audio_path: audioPath });
        job = { ...data, audio_path: audioPath };
        stemJobsRef.current[target] = job;
        toast.info('Splitting stems — vocals will drop the moment they land.');
      }
      const start = Date.now();
      while (Date.now() - start < 5 * 60 * 1000) {
        await new Promise(r => setTimeout(r, 4000));
        const { data } = await api.get(`/ai/stems/${job.job_id}`);
        if (data.status === 'ready' && data.stems) {
          const inst = data.stems.other || data.stems.instrumental || data.stems.drums;
          if (!inst) throw new Error('No instrumental stem returned');
          deck.load(inst, (deck.trackTitle || 'Track') + ' (no vocals)', deck.trackBpm, deck.trackKey);
          setVocalKilled(v => ({ ...v, [target]: true }));
          stemJobsRef.current[target] = { ...job, ...data };
          toast.success(`Vocals killed on deck ${target}`);
          return;
        }
      }
      toast.error('Stem split timed out — try again in a minute.');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.message || 'Stem split failed');
    } finally {
      setVocalBusy(b => ({ ...b, [target]: false }));
    }
  }, [deckA, deckB, demucsEnabled, vocalKilled]);
  // ==============================================================



  // Enforce loops via rAF — jumps currentTime back to loop start when it crosses the end
  useEffect(() => { cuePadRef.current = handleCuePad; loopPadRef.current = handleLoopPad; }, [handleCuePad, handleLoopPad]);
  useEffect(() => {
    let raf;
    const tick = () => {
      const enforce = (audioRef, loops, active) => {
        const audio = audioRef.current;
        if (audio && active >= 0 && loops[active] && loops[active].end_sec != null) {
          if (audio.currentTime >= loops[active].end_sec) {
            audio.currentTime = loops[active].start_sec;
          }
        }
      };
      enforce(deckA.audioEl, loopsA, activeLoopA);
      enforce(deckB.audioEl, loopsB, activeLoopB);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loopsA, loopsB, activeLoopA, activeLoopB, deckA.audioEl, deckB.audioEl]);
  // =========================================

  // ============ Cover art snapshots ============
  const vinylCanvasRef = useRef(null);
  const canvasAnimRef = useRef(null);
  const rotAngleARef = useRef(0);
  const rotAngleBRef = useRef(0);
  const snapshotsRef = useRef([]);
  const snapshotTimerRef = useRef(null);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [pendingMix, setPendingMix] = useState(null);  // {audio_path, duration}
  const [selectedCoverIdx, setSelectedCoverIdx] = useState(0);
  const [savingCover, setSavingCover] = useState(false);
  const [mixTitle, setMixTitle] = useState('');

  // Continuously draw both decks onto a hidden canvas for cover-art + video-track use
  useEffect(() => {
    const cv = vinylCanvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    const draw = () => {
      // Advance rotation based on tempo and play state (0.05 rad per frame at 1x)
      if (deckA.isPlaying) rotAngleARef.current += 0.05 * tempoA;
      if (deckB.isPlaying) rotAngleBRef.current += 0.05 * tempoB;

      const W = cv.width, H = cv.height;
      // Deep black background with gold noise-flecked feel
      ctx.fillStyle = '#050505';
      ctx.fillRect(0, 0, W, H);
      // Subtle radial gold glow
      const grad = ctx.createRadialGradient(W/2, H/2, 40, W/2, H/2, W/1.2);
      grad.addColorStop(0, 'rgba(255,176,0,0.08)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      const drawVinyl = (cx, cy, r, angle, playing) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(angle);
        // vinyl
        ctx.fillStyle = '#000';
        ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill();
        // gold outer ring
        ctx.strokeStyle = 'rgba(255,176,0,0.55)'; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.arc(0, 0, r - 2, 0, Math.PI * 2); ctx.stroke();
        // inner rings
        ctx.strokeStyle = 'rgba(255,176,0,0.15)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(0, 0, r * 0.75, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(0, 0, r * 0.55, 0, Math.PI * 2); ctx.stroke();
        // center label
        ctx.fillStyle = '#FFB000';
        ctx.beginPath(); ctx.arc(0, 0, r * 0.3, 0, Math.PI * 2); ctx.fill();
        // tone-arm marker line
        ctx.strokeStyle = playing ? 'rgba(255,176,0,0.9)' : 'rgba(255,176,0,0.3)';
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(r * 0.32, 0); ctx.lineTo(r - 6, 0); ctx.stroke();
        ctx.restore();
      };

      const r = Math.min(W, H) * 0.22;
      drawVinyl(W * 0.28, H * 0.5, r, rotAngleARef.current, deckA.isPlaying);
      drawVinyl(W * 0.72, H * 0.5, r, rotAngleBRef.current, deckB.isPlaying);

      // Text overlays
      ctx.fillStyle = '#FFB000';
      ctx.font = 'bold 22px Manrope, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('RAW MIXES', W / 2, 38);
      ctx.font = 'bold 12px Manrope, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fillText('LIVE FROM THE DECKS', W / 2, 58);

      const trunc = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s || '—');
      ctx.font = 'bold 14px Manrope, sans-serif';
      ctx.fillStyle = '#fff';
      ctx.fillText(trunc(deckA.trackTitle, 20), W * 0.28, H * 0.5 + r + 30);
      ctx.fillText(trunc(deckB.trackTitle, 20), W * 0.72, H * 0.5 + r + 30);
      ctx.font = '11px monospace';
      ctx.fillStyle = 'rgba(255,176,0,0.8)';
      ctx.fillText(`BPM ${(tempoA * 128).toFixed(1)}`, W * 0.28, H * 0.5 + r + 48);
      ctx.fillText(`BPM ${(tempoB * 128).toFixed(1)}`, W * 0.72, H * 0.5 + r + 48);

      canvasAnimRef.current = requestAnimationFrame(draw);
    };
    canvasAnimRef.current = requestAnimationFrame(draw);
    return () => { if (canvasAnimRef.current) cancelAnimationFrame(canvasAnimRef.current); };
  }, [deckA.isPlaying, deckB.isPlaying, deckA.trackTitle, deckB.trackTitle, tempoA, tempoB]);

  const takeSnapshot = useCallback(() => {
    const cv = vinylCanvasRef.current;
    if (!cv) return null;
    try {
      // Compressed JPEG keeps the snapshot picker snappy
      return cv.toDataURL('image/jpeg', 0.85);
    } catch { return null; }
  }, []);
  // =========================================

  // ============ Go Live (WebRTC broadcaster) ============
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [liveMode, setLiveMode] = useState('audio');   // 'audio' | 'video'
  const [liveTitle, setLiveTitle] = useState('');
  const [liveActive, setLiveActive] = useState(false);
  const [liveInfo, setLiveInfo] = useState(null);      // { stream_id, mode, title, share_url }
  const [viewerCount, setViewerCount] = useState(0);
  const [startingLive, setStartingLive] = useState(false);
  const liveWsRef = useRef(null);
  const livePeersRef = useRef({});                     // viewer_id -> RTCPeerConnection
  const liveStreamRef = useRef(null);                  // MediaStream sent to viewers

  const buildBroadcastStream = useCallback((mode) => {
    const audioStream = getRecordStream();
    if (!audioStream || audioStream.getAudioTracks().length === 0) {
      throw new Error('No audio ready. Hit Play on a deck first.');
    }
    const tracks = [audioStream.getAudioTracks()[0]];
    if (mode === 'video') {
      const cv = vinylCanvasRef.current;
      if (cv && cv.captureStream) {
        const vs = cv.captureStream(24);
        if (vs.getVideoTracks()[0]) tracks.push(vs.getVideoTracks()[0]);
      }
    }
    return new MediaStream(tracks);
  }, [getRecordStream]);

  const openPeerForViewer = useCallback(async (viewerId) => {
    const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
    livePeersRef.current[viewerId] = pc;
    if (liveStreamRef.current) {
      liveStreamRef.current.getTracks().forEach(t => pc.addTrack(t, liveStreamRef.current));
    }
    pc.onicecandidate = (e) => {
      if (e.candidate && liveWsRef.current?.readyState === 1) {
        liveWsRef.current.send(JSON.stringify({ type: 'ice', viewer_id: viewerId, candidate: e.candidate }));
      }
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    liveWsRef.current?.send(JSON.stringify({ type: 'offer', viewer_id: viewerId, sdp: offer }));
  }, []);

  const startLive = async () => {
    if (!liveTitle.trim()) { toast.error('Give your live set a title first'); return; }
    if (!deckA.isPlaying && !deckB.isPlaying) {
      toast.error('Load a track and press Play on a deck before going live');
      return;
    }
    setStartingLive(true);
    try {
      // Ensure audio graph is warm so getRecordStream returns a real stream
      getCtx();
      const stream = buildBroadcastStream(liveMode);
      liveStreamRef.current = stream;

      const { data: meta } = await api.post('/live/start', { title: liveTitle.trim(), mode: liveMode });
      const token = localStorage.getItem('rawmixes_token');
      const wsProto = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
      const wsUrl = `${wsProto}://${BACKEND_URL.replace(/^https?:\/\//, '')}/api/ws/live/${meta.stream_id}?role=broadcaster&token=${encodeURIComponent(token || '')}`;
      const ws = new WebSocket(wsUrl);
      liveWsRef.current = ws;

      ws.onmessage = async (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'broadcaster-ready') {
          setLiveActive(true);
          setShowLiveModal(false);
          setLiveInfo({
            stream_id: meta.stream_id,
            mode: meta.mode,
            title: meta.title,
            share_url: `${window.location.origin}/live/${meta.stream_id}`,
          });
          toast.success('You are live!');
        } else if (msg.type === 'viewer-joined') {
          setViewerCount(c => c + 1);
          openPeerForViewer(msg.viewer_id);
        } else if (msg.type === 'viewer-left') {
          setViewerCount(c => Math.max(0, c - 1));
          const pc = livePeersRef.current[msg.viewer_id];
          if (pc) { try { pc.close(); } catch {} delete livePeersRef.current[msg.viewer_id]; }
        } else if (msg.type === 'answer' && msg.viewer_id) {
          const pc = livePeersRef.current[msg.viewer_id];
          if (pc) { try { await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp)); } catch {} }
        } else if (msg.type === 'ice' && msg.viewer_id && msg.candidate) {
          const pc = livePeersRef.current[msg.viewer_id];
          if (pc) { try { await pc.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch {} }
        } else if (msg.type === 'error') {
          toast.error(msg.error || 'Live error');
        }
      };
      ws.onerror = () => toast.error('Live signaling error');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Could not start live');
    } finally {
      setStartingLive(false);
    }
  };

  const stopLive = async () => {
    const info = liveInfo;
    // Close peers + WS
    Object.values(livePeersRef.current).forEach(pc => { try { pc.close(); } catch {} });
    livePeersRef.current = {};
    try { liveWsRef.current?.close(); } catch {}
    liveWsRef.current = null;
    if (info?.stream_id) {
      try { await api.post(`/live/${info.stream_id}/end`); } catch {}
    }
    setLiveActive(false);
    setLiveInfo(null);
    setViewerCount(0);
    liveStreamRef.current = null;
    // If we were recording, keep it going so the DJ can still choose to post
    // (recording is independent from live). Ask about posting the live set separately:
    if (recording) {
      // let the existing stop button handle it
      toast.info('Live ended. Your recording is still rolling — hit "Stop & post" when you\'re done.');
    } else {
      toast.success('Live ended');
    }
  };

  const copyShareLink = async () => {
    if (!liveInfo?.share_url) return;
    const url = liveInfo.share_url;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied');
        return;
      }
    } catch (_) { /* fall through to prompt fallback below */ }
    // Fallback for insecure contexts / permission denied
    try {
      window.prompt('Copy this share link:', url);
    } catch {
      toast.error('Could not copy the link');
    }
  };
  // =========================================================

  return (
    <Layout>
      {/* Hidden canvas used for cover-art snapshots + live video track */}
      <canvas
        ref={vinylCanvasRef}
        width={720}
        height={480}
        className="hidden"
        aria-hidden="true"
        data-testid="deck-canvas"
      />

      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Live DJ Decks</div>
          <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
            Mix it live.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {liveActive && (
            <div
              className="px-3 py-1.5 rounded-full bg-[#EF4444] text-white text-xs font-bold flex items-center gap-2 shadow-[0_0_20px_rgba(239,68,68,0.5)]"
              data-testid="live-status"
            >
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              LIVE · <Users size={12} /> {viewerCount}
            </div>
          )}
          {hardwareDevice && (
            <div
              className="hidden md:flex px-3 py-1.5 rounded-full border border-[#FFB000]/30 bg-[#FFB000]/5 text-[#FFB000] text-[11px] font-bold items-center gap-2"
              title={`Firmware ${hardwareDevice.firmware_version || '?'}`}
              data-testid="hardware-status"
            >
              <CheckCircle2 size={12} /> {hardwareDevice.device_name}
              {hardwareDevice.serial && <span className="font-mono opacity-70">#{hardwareDevice.serial.slice(-6)}</span>}
            </div>
          )}
          <button
            onClick={() => setShowMappingModal(true)}
            className="btn-pill btn-outline text-xs flex items-center gap-1.5"
            data-testid="open-mapping-btn"
            title="Customise MIDI mapping"
          >
            <Settings size={12} /> Controls
          </button>
          <button
            onClick={startDiagnostics}
            className="btn-pill btn-outline text-xs flex items-center gap-1.5"
            data-testid="open-diagnostics-btn"
            title="Test every pad and knob"
          >
            <Zap size={12} /> Diagnostics
          </button>
          <button
            onClick={openFirmwareModal}
            className="btn-pill btn-outline text-xs flex items-center gap-1.5"
            data-testid="open-firmware-btn"
            title="Firmware updater"
          >
            <Download size={12} /> Firmware
          </button>
          <div
            className={`px-3 py-1.5 rounded-full border text-xs font-bold flex items-center gap-2 ${
              midiStatus === 'connected' ? 'border-[#22C55E]/40 text-[#22C55E] bg-[#22C55E]/5'
              : midiStatus === 'scanning' ? 'border-[#FFB000]/30 text-[#FFB000] bg-[#FFB000]/5'
              : 'border-white/10 text-[#A1A1AA]'
            }`}
            data-testid="midi-status"
          >
            <span className={`w-2 h-2 rounded-full ${midiStatus === 'connected' ? 'bg-[#22C55E] animate-pulse' : 'bg-[#A1A1AA]'}`}/>
            {midiStatus === 'connected' && `Mini Deck: ${midiDeviceName}`}
            {midiStatus === 'scanning' && 'Waiting for Mini Deck...'}
            {midiStatus === 'unsupported' && 'MIDI unavailable in this browser'}
            {midiStatus === 'denied' && 'MIDI access denied'}
          </div>
        </div>
      </div>

      {/* Deck row — DeckA · CenterConsole · DeckB reads as one mixer */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px_1fr] gap-3 mb-4">
        <DeckPanel
          label="Deck A" side="a" deck={deckA}
          tempo={tempoA} setTempo={setTempoA}
          low={lowA} setLow={setLowA} mid={midA} setMid={setMidA} high={highA} setHigh={setHighA}
          filter={filterA} setFilter={setFilterA}
          volume={gainA}
          cues={cuesA} loops={loopsA} activeLoop={activeLoopA}
          onCue={(i, shift) => handleCuePad('A', i, shift)}
          onLoop={(i, shift) => handleLoopPad('A', i, shift)}
          onVocalKill={() => toggleVocalKill('A')}
          vocalKilled={vocalKilled.A}
          vocalBusy={vocalBusy.A}
          demucsEnabled={demucsEnabled}
        />
        <CenterConsole
          deckA={deckA} deckB={deckB}
          tempoA={tempoA} setTempoA={setTempoA}
          tempoB={tempoB} setTempoB={setTempoB}
          masterVol={masterVol} setMasterVol={setMasterVol}
          crossfade={crossfade} setCrossfade={setCrossfade}
          recording={recording} savingMix={savingMix}
          startRecording={startRecording}
          stopRecordingAndPost={stopRecordingAndPost}
          liveActive={liveActive}
          liveViewers={viewerCount}
          onGoLiveClick={() => { setLiveTitle(`Live from the decks · ${new Date().toLocaleDateString()}`); setShowLiveModal(true); }}
          liveButtonRightSlot={liveActive ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={stopLive}
                className="h-9 rounded-full flex items-center justify-center gap-2 bg-[#EF4444] hover:bg-[#dc3838] text-white font-heading font-black text-[10px] uppercase tracking-widest transition-colors"
                data-testid="end-live-btn"
              >
                <X size={12} /> End live
              </button>
              <button
                onClick={copyShareLink}
                className="h-9 rounded-full flex items-center justify-center gap-1 border border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10 font-heading font-black text-[10px] uppercase tracking-widest transition-colors"
                data-testid="copy-live-link-btn"
              >
                Copy link
              </button>
            </div>
          ) : null}
        />
        <DeckPanel
          label="Deck B" side="b" deck={deckB}
          tempo={tempoB} setTempo={setTempoB}
          low={lowB} setLow={setLowB} mid={midB} setMid={setMidB} high={highB} setHigh={setHighB}
          filter={filterB} setFilter={setFilterB}
          volume={gainB}
          cues={cuesB} loops={loopsB} activeLoop={activeLoopB}
          onCue={(i, shift) => handleCuePad('B', i, shift)}
          onLoop={(i, shift) => handleLoopPad('B', i, shift)}
          onVocalKill={() => toggleVocalKill('B')}
          vocalKilled={vocalKilled.B}
          vocalBusy={vocalBusy.B}
          demucsEnabled={demucsEnabled}
        />
      </div>

      {/* Status strip — REC timer / posting / broadcasting hint */}
      <div className="rounded-2xl bg-black/40 border border-white/5 px-4 py-2 mb-6 flex items-center gap-3 text-sm min-h-[36px]" data-testid="deck-status-strip">
        {recording && (
          <>
            <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse flex-shrink-0" />
            <span className="text-[#EF4444] font-bold font-mono" data-testid="record-timer">
              REC {Math.floor(recordSecs / 60)}:{(recordSecs % 60).toString().padStart(2, '0')}
            </span>
          </>
        )}
        {savingMix && <span className="text-[#FFB000] text-xs animate-pulse">Posting to feed…</span>}
        {!recording && !savingMix && !liveActive && (
          <span className="text-[#A1A1AA] text-xs">Capture your mix or go live to your followers.</span>
        )}
        {liveActive && (
          <span className="text-[#A1A1AA] text-xs">
            Broadcasting {liveInfo?.mode === 'video' ? 'audio + vinyl visual' : 'audio-only'} · {viewerCount} listening
          </span>
        )}
      </div>

      {/* Track library */}
      <div className="post-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFB000]">Your library</div>
            <div className="text-white font-heading font-bold text-lg">Load a track</div>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,video/*"
              onChange={(e) => uploadTarget && handleFileUpload(e, uploadTarget)}
              className="hidden"
              data-testid="deck-upload-input"
            />
            <button
              onClick={() => { setUploadTarget('A'); fileInputRef.current?.click(); }}
              disabled={uploading}
              className="btn-pill btn-outline text-xs flex items-center gap-1"
              data-testid="deck-upload-a"
            >
              <UploadIcon size={12} /> A
            </button>
            <button
              onClick={() => { setUploadTarget('B'); fileInputRef.current?.click(); }}
              disabled={uploading}
              className="btn-pill btn-outline text-xs flex items-center gap-1"
              data-testid="deck-upload-b"
            >
              <UploadIcon size={12} /> B
            </button>
          </div>
        </div>
        {uploading && (
          <div className="text-[#FFB000] text-sm mb-3 animate-pulse">Uploading &amp; converting...</div>
        )}
        {anchorTrack && ghostSuggestions.length > 0 && (
          <div className="mb-4 p-3 rounded-xl border border-[#FFB000]/30 bg-[#FFB000]/5" data-testid="track-ghost-panel">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFB000]">Track Ghost</span>
              <span className="text-[10px] text-[#A1A1AA]">Next tracks that key-lock into the deck</span>
            </div>
            <div className="space-y-1">
              {ghostSuggestions.map(p => (
                <div key={`ghost-${p.id}`} className="flex items-center gap-3 p-2 rounded-lg bg-black/30 hover:bg-black/50 transition-colors" data-testid={`ghost-${p.id}`}>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${p.__ghost.tone === 'perfect' ? 'bg-[#22C55E]' : p.__ghost.tone === 'great' ? 'bg-[#FFB000]' : 'bg-[#A1A1AA]'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-bold truncate">{p.title}</div>
                    <div className="text-[10px] text-[#A1A1AA] truncate">
                      <span className={`font-bold ${p.__ghost.tone === 'perfect' ? 'text-[#22C55E]' : p.__ghost.tone === 'great' ? 'text-[#FFB000]' : 'text-[#A1A1AA]'}`}>{p.__ghost.badge}</span>
                      {p.bpm && <span className="font-mono ml-2">{p.bpm}</span>}
                      {p.musical_key && <span className="font-mono ml-1">{p.musical_key}</span>}
                    </div>
                  </div>
                  <button onClick={() => loadIntoDeck(deckA, p)} className="text-[10px] font-bold text-[#FFB000] hover:text-white px-2 py-1" data-testid={`ghost-load-a-${p.id}`}>A</button>
                  <button onClick={() => loadIntoDeck(deckB, p)} className="text-[10px] font-bold text-[#FFB000] hover:text-white px-2 py-1" data-testid={`ghost-load-b-${p.id}`}>B</button>
                </div>
              ))}
            </div>
          </div>
        )}
        {library.length === 0 ? (
          <p className="text-[#A1A1AA] text-sm">No tracks yet. Upload a mix in the Feed first, or tap A/B above to import a file.</p>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {scoredLibrary.map(p => (
              <div key={p.id} className={`flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 ${p.__ghost ? 'ring-1 ring-[#FFB000]/20' : ''}`} data-testid={`lib-track-${p.id}`}>
                <Music size={16} className="text-[#FFB000] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-bold truncate flex items-center gap-2">
                    {p.title}
                    {p.__ghost && (
                      <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${p.__ghost.tone === 'perfect' ? 'bg-[#22C55E]/20 text-[#22C55E]' : p.__ghost.tone === 'great' ? 'bg-[#FFB000]/20 text-[#FFB000]' : 'bg-white/10 text-[#A1A1AA]'}`}>
                        {p.__ghost.label}
                      </span>
                    )}
                  </div>
                  <div className="text-[#A1A1AA] text-xs truncate flex items-center gap-1.5">
                    <span className="truncate">{p.author?.name} · {p.genre}</span>
                    {p.bpm && <span className="font-mono text-[#FFB000] flex-shrink-0">· {p.bpm}</span>}
                    {p.musical_key && <span className="font-mono text-[#FFB000] flex-shrink-0">· {p.musical_key}</span>}
                  </div>
                </div>
                <button
                  onClick={() => loadIntoDeck(deckA, p)}
                  className="text-xs font-bold text-[#FFB000] hover:text-[#E69E00] px-2 py-1"
                  data-testid={`lib-load-a-${p.id}`}
                >A</button>
                <button
                  onClick={() => loadIntoDeck(deckB, p)}
                  className="text-xs font-bold text-[#FFB000] hover:text-[#E69E00] px-2 py-1"
                  data-testid={`lib-load-b-${p.id}`}
                >B</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Go Live modal */}
      {showLiveModal && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="go-live-modal">
          <div className="post-card p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#EF4444]">Go Live</div>
                <div className="text-white font-heading font-black text-2xl">Broadcast your set</div>
              </div>
              <button onClick={() => setShowLiveModal(false)} className="text-[#A1A1AA] hover:text-white" data-testid="live-modal-close"><X size={18} /></button>
            </div>
            <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Title</label>
            <input
              value={liveTitle}
              onChange={e => setLiveTitle(e.target.value)}
              placeholder="Friday night warm-up"
              className="input-default mb-4"
              data-testid="live-title-input"
            />
            <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Broadcast mode</label>
            <div className="grid grid-cols-2 gap-2 mb-6">
              <button
                onClick={() => setLiveMode('audio')}
                className={`p-3 rounded-xl border text-left transition-all ${liveMode === 'audio' ? 'border-[#FFB000] bg-[#FFB000]/10' : 'border-white/10 hover:border-white/20'}`}
                data-testid="live-mode-audio"
              >
                <div className="flex items-center gap-2 mb-1"><Mic size={14} className="text-[#FFB000]" /><span className="font-heading font-bold text-white text-sm">Audio-only</span></div>
                <div className="text-[10px] text-[#A1A1AA] leading-relaxed">Cleanest sound, lowest bandwidth. Pure DJ set.</div>
              </button>
              <button
                onClick={() => setLiveMode('video')}
                className={`p-3 rounded-xl border text-left transition-all ${liveMode === 'video' ? 'border-[#FFB000] bg-[#FFB000]/10' : 'border-white/10 hover:border-white/20'}`}
                data-testid="live-mode-video"
              >
                <div className="flex items-center gap-2 mb-1"><Video size={14} className="text-[#FFB000]" /><span className="font-heading font-bold text-white text-sm">Audio + vinyl</span></div>
                <div className="text-[10px] text-[#A1A1AA] leading-relaxed">Ships the spinning vinyl visual with your mix.</div>
              </button>
            </div>
            <button
              onClick={startLive}
              disabled={startingLive || !liveTitle.trim()}
              className="btn-pill btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="live-start-btn"
            >
              {startingLive ? 'Going live…' : (<><Radio size={14} /> Start broadcast</>)}
            </button>
            <div className="text-[10px] text-[#A1A1AA] text-center mt-3 leading-relaxed">
              Small audiences work great on the free tier. If you outgrow it, we can plug in a media server without changing your flow.
            </div>
          </div>
        </div>
      )}

      {/* Cover picker after stopping a recording */}
      {showCoverPicker && pendingMix && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cover-picker-modal">
          <div className="post-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFB000]">Auto cover art</div>
                <div className="text-white font-heading font-black text-2xl">Name it & pick a cover</div>
                <div className="text-[#A1A1AA] text-xs mt-1">Frames grabbed live from your decks while you mixed.</div>
              </div>
              <button
                onClick={() => { setShowCoverPicker(false); setPendingMix(null); setMixTitle(''); snapshotsRef.current = []; }}
                className="text-[#A1A1AA] hover:text-white text-xs font-bold"
                data-testid="cover-cancel-btn"
              >
                Cancel
              </button>
            </div>
            <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Mix title</label>
            <input
              value={mixTitle}
              onChange={e => setMixTitle(e.target.value)}
              placeholder={`Live Mix · ${new Date().toLocaleDateString()}`}
              className="input-default mb-5"
              data-testid="mix-title-input"
              autoFocus
            />
            <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Cover art</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {snapshotsRef.current.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedCoverIdx(i)}
                  className={`relative rounded-xl overflow-hidden border-2 transition-all ${selectedCoverIdx === i ? 'border-[#FFB000] shadow-[0_0_20px_rgba(255,176,0,0.4)]' : 'border-white/10 hover:border-white/30'}`}
                  data-testid={`cover-option-${i}`}
                  aria-label={`Snapshot at ${s.at} seconds`}
                >
                  <img src={s.dataUrl} alt={`Snapshot at ${s.at}s`} className="w-full aspect-video object-cover" />
                  <div className="absolute bottom-1 left-1 bg-black/70 text-[#FFB000] text-[10px] font-mono px-1.5 py-0.5 rounded">
                    {Math.floor(s.at / 60)}:{(s.at % 60).toString().padStart(2, '0')}
                  </div>
                  {selectedCoverIdx === i && (
                    <div className="absolute top-1 right-1 bg-[#FFB000] text-black rounded-full p-0.5">
                      <CheckCircle2 size={14} />
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => finalisePost(pendingMix, null)}
                disabled={savingCover}
                className="btn-pill btn-outline flex-1 text-sm disabled:opacity-40"
                data-testid="cover-skip-btn"
              >
                Post without cover
              </button>
              <button
                onClick={() => finalisePost(pendingMix, snapshotsRef.current[selectedCoverIdx]?.dataUrl)}
                disabled={savingCover}
                className="btn-pill btn-primary flex-[2] flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="cover-confirm-btn"
              >
                {savingCover ? 'Posting…' : 'Use this cover & post mix'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MIDI mapping modal */}
      {showMappingModal && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mapping-modal">
          <div className="post-card p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFB000]">Custom controls</div>
                <div className="text-white font-heading font-black text-2xl">Map your deck</div>
                <div className="text-[#A1A1AA] text-xs mt-1">
                  Click <span className="text-[#FFB000] font-bold">Learn</span> next to any function, then wiggle the pad, knob or fader on your deck to bind it.
                </div>
              </div>
              <button onClick={() => { setShowMappingModal(false); setLearnFn(null); }} className="text-[#A1A1AA] hover:text-white" data-testid="mapping-close-btn"><X size={18} /></button>
            </div>

            {learnFn && (
              <div className="mb-4 p-3 rounded-xl bg-[#FFB000]/10 border border-[#FFB000]/40 text-[#FFB000] text-sm flex items-center justify-between" data-testid="mapping-learn-hint">
                <div className="flex items-center gap-2"><Zap size={14} /> Waiting for input for <span className="font-bold">{learnFn.label}</span>…</div>
                <button onClick={() => setLearnFn(null)} className="text-xs underline">Cancel</button>
              </div>
            )}

            <div className="space-y-1 mb-5">
              {FUNCTION_CATALOG.map(fn => {
                const bound = Object.entries(midiMap).find(([, v]) => v.function === fn.key);
                return (
                  <div key={fn.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5" data-testid={`mapping-row-${fn.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-bold truncate">{fn.label}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">{fn.kind === 'toggle' ? 'Button / pad' : 'Knob / fader'}</div>
                    </div>
                    <div className="text-xs font-mono px-2 py-1 rounded bg-black/40 text-[#FFB000] min-w-[110px] text-center">
                      {bound ? bound[0] : 'unassigned'}
                    </div>
                    <button
                      onClick={() => setLearnFn(fn)}
                      disabled={midiStatus !== 'connected'}
                      className={`btn-pill text-xs ${learnFn?.key === fn.key ? 'btn-primary' : 'btn-outline'} disabled:opacity-40`}
                      data-testid={`mapping-learn-${fn.key}`}
                    >
                      {learnFn?.key === fn.key ? 'Listening…' : 'Learn'}
                    </button>
                    <button
                      onClick={() => clearBinding(fn.key)}
                      disabled={!bound}
                      className="text-[#A1A1AA] hover:text-[#EF4444] text-xs disabled:opacity-30"
                      data-testid={`mapping-clear-${fn.key}`}
                      title="Clear this binding"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-white/5">
              <button onClick={resetMapping} className="text-xs text-[#A1A1AA] hover:text-[#EF4444]" data-testid="mapping-reset-btn">Reset to factory mapping</button>
              <button onClick={() => { setShowMappingModal(false); setLearnFn(null); }} className="btn-pill btn-primary text-sm" data-testid="mapping-done-btn">Done</button>
            </div>
            {midiStatus !== 'connected' && (
              <div className="mt-3 text-xs text-[#A1A1AA] text-center">
                Connect your Mini Deck over USB to learn new bindings.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Firmware modal */}
      {showFirmwareModal && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="firmware-modal">
          <div className="post-card p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFB000]">Firmware</div>
                <div className="text-white font-heading font-black text-2xl">Keep your deck sharp</div>
              </div>
              <button onClick={() => setShowFirmwareModal(false)} className="text-[#A1A1AA] hover:text-white" data-testid="firmware-close-btn"><X size={18} /></button>
            </div>
            {!firmwareManifest && <div className="text-[#A1A1AA] text-sm">Checking for updates…</div>}
            {firmwareManifest && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-xl border border-white/10 bg-black/40">
                    <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Installed</div>
                    <div className="text-white font-mono font-bold text-lg" data-testid="firmware-installed">{hardwareDevice?.firmware_version || 'Not detected'}</div>
                  </div>
                  <div className="p-3 rounded-xl border border-[#FFB000]/40 bg-[#FFB000]/5">
                    <div className="text-[10px] uppercase tracking-widest text-[#FFB000] font-bold mb-1">Latest</div>
                    <div className="text-[#FFB000] font-mono font-bold text-lg" data-testid="firmware-latest">{firmwareManifest.latest_version}</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Release notes</div>
                <ul className="text-white text-sm space-y-1 mb-5 list-disc pl-5" data-testid="firmware-notes">
                  {firmwareManifest.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
                {(!hardwareDevice || (() => {
                  const parse = v => (v || '').replace(/[^0-9.]/g, '').split('.').map(x => parseInt(x, 10) || 0);
                  const a = parse(hardwareDevice.firmware_version), b = parse(firmwareManifest.latest_version);
                  for (let i = 0; i < Math.max(a.length, b.length); i++) {
                    const d = (a[i] || 0) - (b[i] || 0);
                    if (d !== 0) return d < 0;  // installed < latest → update available
                  }
                  return false;
                })()) ? (
                  <button
                    onClick={pushFirmware}
                    disabled={!midiOutput || firmwareStatus === 'pushing'}
                    className="btn-pill btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40"
                    data-testid="firmware-push-btn"
                  >
                    <Download size={14} />
                    {firmwareStatus === 'pushing' ? 'Pushing over SysEx…' :
                     firmwareStatus === 'done' ? 'Update sent — reboot the deck' :
                     'Push firmware to deck'}
                  </button>
                ) : (
                  <div className="text-center text-[#22C55E] text-sm font-bold" data-testid="firmware-up-to-date">Your deck is up to date.</div>
                )}
                {!midiOutput && (
                  <div className="mt-3 text-xs text-[#A1A1AA] text-center">
                    Plug the deck in via USB to receive the update.
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                  <div className="text-[10px] text-[#A1A1AA] font-mono">
                    SHA-256 {firmwareManifest.sha256.slice(0, 20)}…
                  </div>
                  <button
                    onClick={openWarranty}
                    disabled={!hardwareDevice}
                    className="text-xs text-[#FFB000] hover:text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="firmware-warranty-link"
                    title={hardwareDevice ? '' : 'Connect your deck first'}
                  >
                    Register warranty →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Diagnostics modal */}
      {showDiagnosticsModal && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="diagnostics-modal">
          <div className="post-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFB000]">Diagnostics</div>
                <div className="text-white font-heading font-black text-2xl">Test every pad, knob & fader</div>
                <div className="text-[#A1A1AA] text-xs mt-1">
                  Touch every control on your deck. Each one lights up here the moment it fires. {Object.keys(diagHits).length} unique inputs detected.
                </div>
              </div>
              <button onClick={() => setShowDiagnosticsModal(false)} className="text-[#A1A1AA] hover:text-white" data-testid="diagnostics-close-btn"><X size={18} /></button>
            </div>

            {midiStatus !== 'connected' && (
              <div className="mb-4 p-3 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] text-sm">
                Plug in your Mini Deck via USB — Diagnostics needs a live MIDI connection.
              </div>
            )}

            {Object.keys(diagHits).length === 0 ? (
              <div className="py-16 text-center text-[#A1A1AA] text-sm border border-dashed border-white/10 rounded-xl">
                <Zap size={32} className="mx-auto mb-3 text-[#FFB000]/40" />
                Waiting for input… try every pad and knob on the deck.
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-5" data-testid="diagnostics-grid">
                {Object.entries(diagHits).map(([k, v]) => (
                  <div
                    key={k}
                    className="p-3 rounded-xl border border-[#22C55E]/40 bg-[#22C55E]/5"
                    data-testid={`diag-hit-${k}`}
                  >
                    <div className="text-white font-heading font-bold text-sm">{v.kind === 'note' ? 'Pad' : 'Knob'}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-mono mb-1">{k}</div>
                    <div className="text-xs text-[#22C55E] font-mono">
                      hits {v.count} · last {v.lastVal}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 pt-3 border-t border-white/5">
              <button onClick={() => setDiagHits({})} className="btn-pill btn-outline text-xs flex-1" data-testid="diagnostics-reset-btn">
                Reset hits
              </button>
              <button onClick={() => setShowDiagnosticsModal(false)} className="btn-pill btn-primary text-xs flex-1" data-testid="diagnostics-done-btn">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warranty modal */}
      {showWarrantyModal && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="warranty-modal">
          <div className="post-card p-6 max-w-md w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFB000]">Warranty</div>
                <div className="text-white font-heading font-black text-2xl">Register your deck</div>
                <div className="text-[#A1A1AA] text-xs mt-1">Serial and firmware are pulled straight from the deck.</div>
              </div>
              <button onClick={() => setShowWarrantyModal(false)} className="text-[#A1A1AA] hover:text-white" data-testid="warranty-close-btn"><X size={18} /></button>
            </div>

            {hardwareDevice && (
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="p-3 rounded-xl border border-[#FFB000]/40 bg-[#FFB000]/5">
                  <div className="text-[10px] uppercase tracking-widest text-[#FFB000] font-bold mb-1">Serial</div>
                  <div className="text-white font-mono text-sm font-bold" data-testid="warranty-serial">{hardwareDevice.serial || 'Unknown'}</div>
                </div>
                <div className="p-3 rounded-xl border border-white/10 bg-black/40">
                  <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Firmware</div>
                  <div className="text-white font-mono text-sm font-bold" data-testid="warranty-firmware">{hardwareDevice.firmware_version || 'Unknown'}</div>
                </div>
              </div>
            )}

            {warrantyExisting ? (
              <div className="p-3 mb-4 rounded-xl bg-[#22C55E]/10 border border-[#22C55E]/40 text-[#22C55E] text-sm flex items-center gap-2" data-testid="warranty-registered">
                <CheckCircle2 size={16} /> Registered on {new Date(warrantyExisting.registered_at).toLocaleDateString()}
              </div>
            ) : null}

            <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Owner name</label>
            <input value={warranty.owner_name} onChange={e => setWarranty({ ...warranty, owner_name: e.target.value })} className="input-default mb-3" data-testid="warranty-name-input" />

            <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Email</label>
            <input type="email" value={warranty.email} onChange={e => setWarranty({ ...warranty, email: e.target.value })} className="input-default mb-3" data-testid="warranty-email-input" />

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Phone</label>
                <input value={warranty.phone} onChange={e => setWarranty({ ...warranty, phone: e.target.value })} className="input-default" data-testid="warranty-phone-input" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Country</label>
                <input value={warranty.country} onChange={e => setWarranty({ ...warranty, country: e.target.value.toUpperCase().slice(0, 2) })} className="input-default" data-testid="warranty-country-input" />
              </div>
            </div>

            <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Purchased from</label>
            <input value={warranty.purchased_from} onChange={e => setWarranty({ ...warranty, purchased_from: e.target.value })} placeholder="e.g. rawmixes.uk store" className="input-default mb-5" data-testid="warranty-purchased-input" />

            <button
              onClick={submitWarranty}
              disabled={warrantyBusy || !warranty.owner_name.trim() || !warranty.email.trim()}
              className="btn-pill btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="warranty-submit-btn"
            >
              {warrantyBusy ? 'Registering…' : (warrantyExisting ? 'Update warranty' : 'Register warranty')}
            </button>
            <div className="mt-3 text-[10px] text-[#A1A1AA] text-center leading-relaxed">
              2-year limited warranty · RAW COMPANY TECH LIMITED
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Decks;
